// Realtime push client: singola connessione WebSocket verso /api/ws.
// Alla ricezione di un evento {topic} emette un CustomEvent 'aos:realtime'
// che i moduli ascoltano per ricaricarsi entro pochi ms (collaborazione live).
import { useEffect, useRef } from "react";
import { tokenStore, api } from "@/lib/api";

let _ws = null;
let _retry = 0;
let _closedByUser = false;
let _pollTimer = null;
let _pollSince = null;
let _pollHandle = null;

// Polling HTTP: fallback affidabile e indipendente dal WebSocket (funziona anche con più
// pod in produzione o se l'ingress non fa upgrade del WS). Ogni ~0,3s chiede i topic con
// eventi recenti e li ridistribuisce come 'aos:realtime', esattamente come farebbe il WS.
async function _pollOnce() {
  if (!tokenStore.get()) return;
  try {
    const { data } = await api.get("/realtime/poll", _pollSince ? { params: { since: _pollSince } } : {});
    if (data && data.now) {
      const prev = _pollSince;
      _pollSince = data.now;
      if (prev) {
        (data.topics || []).forEach((t) =>
          window.dispatchEvent(new CustomEvent("aos:realtime", { detail: { topic: t, at: data.now } })));
      }
    }
  } catch { /* silenzioso: riprova al prossimo giro */ }
}

let _pollStop = false;

export function startRealtimePolling() {
  if (_pollTimer) return;
  _pollSince = null;
  _pollStop = false;
  _pollTimer = true; // segnala "attivo" (il ciclo si auto-ripianifica)
  const loop = async () => {
    if (_pollStop) return;
    await _pollOnce();               // attende il completamento: niente richieste accavallate
    if (_pollStop) return;
    _pollHandle = setTimeout(loop, 300);
  };
  loop();
}

export function stopRealtimePolling() {
  _pollStop = true;
  if (_pollHandle) { clearTimeout(_pollHandle); _pollHandle = null; }
  _pollTimer = null;
  _pollSince = null;
}

const wsUrl = () => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const proto = base.startsWith("https") ? "wss" : "ws";
  const host = base.replace(/^https?:\/\//, "");
  const token = tokenStore.get() || "";
  return `${proto}://${host}/api/ws?token=${encodeURIComponent(token)}`;
};

export function connectRealtime() {
  if (!tokenStore.get()) return;
  startRealtimePolling();
  if (_ws && (_ws.readyState === 0 || _ws.readyState === 1)) return;
  _closedByUser = false;
  try {
    _ws = new WebSocket(wsUrl());
  } catch { return; }
  _ws.onopen = () => { _retry = 0; };
  _ws.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (msg && msg.topic) {
        window.dispatchEvent(new CustomEvent("aos:realtime", { detail: msg }));
      }
    } catch { /* ignore */ }
  };
  _ws.onclose = () => {
    if (_closedByUser) return;
    const delay = Math.min(1000 * 2 ** _retry, 15000);
    _retry += 1;
    setTimeout(() => connectRealtime(), delay);
  };
  _ws.onerror = () => { try { _ws.close(); } catch { /* noop */ } };
}

export function disconnectRealtime() {
  _closedByUser = true;
  stopRealtimePolling();
  try { _ws && _ws.close(); } catch { /* noop */ }
  _ws = null;
}

// Hook: richiama `cb` quando arriva un evento su uno dei `topics` (array o stringa).
export function useRealtime(topics, cb) {
  const cbRef = useRef(cb);
  cbRef.current = cb;
  useEffect(() => {
    const list = Array.isArray(topics) ? topics : [topics];
    const handler = (e) => {
      const t = e.detail?.topic;
      if (t && list.includes(t)) cbRef.current?.(e.detail);
    };
    window.addEventListener("aos:realtime", handler);
    return () => window.removeEventListener("aos:realtime", handler);
  }, [Array.isArray(topics) ? topics.join(",") : topics]); // eslint-disable-line react-hooks/exhaustive-deps
}
